/**
 * MRDS veri katmani.
 *
 * Eskiden bu dosyanin yerinde `supabaseApi.js` vardi: tarayici dogrudan
 * Supabase PostgREST'e baglanir, 12 ham tabloyu cekip join/hesaplama
 * islerini JavaScript'te yapardi.
 *
 * Artik veriler sunucudaki MRDS API'sinden geliyor. Join, gruplama ve
 * pivot isleri SQL'de yapiliyor -- tarayici sadece hazir sonucu aliyor.
 * Bu hem cok daha hizli hem de aktarilan veri miktari kucuk.
 *
 * ONEMLI: Disari verilen namespace'ler (appsApi, storeMetricsApi,
 * subscriptionsApi, ga4Api, socialApi, unifiedApi, dashboardApi) ve
 * dondukleri obje sekilleri supabaseApi.js ile BIREBIR AYNI. Sayfalarda
 * degisiklik gerekmez -- sadece import yolu degisir.
 *
 * Kimlik dogrulama: istekler Cloudflare Pages Function uzerinden
 * proxy'lenir (functions/api/mrds/[[path]].js); X-API-Key sunucu
 * tarafinda eklenir, tarayiciya hicbir zaman gomulmez.
 */

import axios from 'axios';

// ---------------------------------------------------------------------------
// HTTP istemcisi
// ---------------------------------------------------------------------------

// Istekler AYNI ORIGIN'e, goreli yola gider: /api/mrds/api/v1/...
//
// Bu yolu Cloudflare Pages Function karsilar
// (functions/api/mrds/[[path]].js): X-API-Key'i sunucu tarafinda
// ekleyip istegi gercek MRDS API'sine iletir. Anahtar tarayiciya
// hicbir zaman gitmez ve ayni origin oldugu icin CORS da gerekmez.
//
// DIKKAT: REACT_APP_API_BASE_URL burada KULLANILMAZ. O degisken
// social media servisinin adresini tutuyor (socialapi.collabry.io) --
// MRDS ile ilgisi yok. Ona yonlendirmek CORS hatasina yol acar.
//
// REACT_APP_MRDS_API_URL yalnizca ozel durumlar icin: Pages disinda
// bir ortamda calisirken (or. yerel gelistirme) tam URL vermek
// isterseniz.
const API_BASE =
  process.env.REACT_APP_MRDS_API_URL || '/api/mrds/api/v1';

const client = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    console.error(
      `❌ MRDS ${err.config?.method?.toUpperCase()} ${err.config?.url}`,
      err.response?.status,
      err.response?.data,
    );
    return Promise.reject(err);
  },
);

/**
 * API her zaman { data: [...], meta: {...} } dondurur.
 * meta.warning dolu ise veri bayat demektir -- konsola yazip devam ederiz,
 * cagiran taraf isterse meta'ya bakabilir.
 */
const get = async (path, params = {}) => {
  const res = await client.get(path, { params });
  const body = res.data || {};
  if (body.meta?.warning) {
    console.warn(`⚠️ MRDS ${path}: ${body.meta.warning}`);
  }
  return { rows: body.data || [], meta: body.meta || {} };
};

// ---------------------------------------------------------------------------
// Onbellek
//
// Sayfalar arasi gecislerde ayni endpoint'i tekrar tekrar cagirmamak icin
// kisa omurlu bir onbellek. supabaseApi.js'teki refreshCache() ayni isi
// goruyordu; API sozlesmesini korumak icin burada da var.
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 60_000;
const cache = new Map();

const cached = async (key, loader) => {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.value;
  const value = await loader();
  cache.set(key, { at: Date.now(), value });
  return value;
};

export const refreshCache = () => {
  cache.clear();
};

const PLATFORM_IOS = 'ios';
const PLATFORM_ANDROID = 'android';
const PLATFORM_WEB = 'web';

// ---------------------------------------------------------------------------
// Uygulama katalogu
// ---------------------------------------------------------------------------

export const appsApi = {
  /** Returns: [{ app_name, platforms: [{ type, app_extid }] }] */
  async getAll() {
    return cached('apps', async () => {
      const { rows } = await get('/apps');
      return rows;
    });
  },
};

// ---------------------------------------------------------------------------
// Magaza metrikleri (indirme + puan)
// ---------------------------------------------------------------------------

export const storeMetricsApi = {
  /**
   * Uygulama basina kumulatif indirme.
   *
   * Returns: [{ app_platform_id, app_name, platform, app_extid, total_downloads,
   *             by_country: [{ country, currency, downloads }],
   *             by_date?: [{ report_date, downloads }] }]
   *
   * @param {Object} opts
   * @param {string} [opts.platform]        'ios' | 'android' | 'web'
   * @param {string} [opts.appName]         tam uygulama adi
   * @param {number} [opts.timeseriesDays]  verilirse by_date dizisi eklenir
   */
  async getDownloads(opts = {}) {
    const { platform, appName, timeseriesDays } = opts;
    const key = `downloads:${platform || ''}:${appName || ''}:${timeseriesDays || ''}`;
    return cached(key, async () => {
      const params = {};
      if (platform) params.platform = platform;
      if (appName) params.app_name = appName;
      if (timeseriesDays) {
        params.include_timeseries = true;
        params.days = timeseriesDays;
      }
      const { rows } = await get('/downloads', params);
      // by_date her zaman dizi olsun -- cagiran taraf kontrol etmesin.
      return rows.map((r) => ({ ...r, by_date: r.by_date || [] }));
    });
  },

  /**
   * Uygulama basina ortalama puan + son snapshot.
   *
   * Returns: [{ app_platform_id, app_name, platform, app_extid, avg_rating,
   *             snapshot_count, latest_rating, latest_date, total_ratings }]
   */
  async getRatings(opts = {}) {
    const { platform, appName } = opts;
    const key = `ratings:${platform || ''}:${appName || ''}`;
    return cached(key, async () => {
      const params = {};
      if (platform) params.platform = platform;
      if (appName) params.app_name = appName;
      const { rows } = await get('/ratings', params);
      return rows;
    });
  },
};

// ---------------------------------------------------------------------------
// Abonelikler
// ---------------------------------------------------------------------------

export const subscriptionsApi = {
  /**
   * Returns: {
   *   rows: [{ app_name, platform, total_active_subscribers,
   *            by_subscription_type: [{ name, duration, active_subscribers,
   *              by_country: [{ country, currency, active_subscribers,
   *                             customer_price, developer_proceeds }] }] }],
   *   asOfDate: 'YYYY-MM-DD' | null,
   *   warning:  string | null
   * }
   */
  async getLatest({ maxAgeDays = 7 } = {}) {
    return cached(`subs:${maxAgeDays}`, async () => {
      const { rows, meta } = await get('/subscriptions', {
        max_age_days: maxAgeDays,
      });
      return {
        rows,
        asOfDate: meta.as_of_date || null,
        warning: meta.warning || null,
      };
    });
  },
};

// ---------------------------------------------------------------------------
// GA4 / web metrikleri
// ---------------------------------------------------------------------------

export const ga4Api = {
  /**
   * Returns: {
   *   rows: [{ app_platform_id, property_name, app_extid, report_date,
   *            active_users, new_users, returning_users, views,
   *            event_counts, average_session_duration_seconds,
   *            user_acquisitions: { organic_search, direct, referrals } }],
   *   asOfDate, warning
   * }
   */
  async getLatest({ maxAgeDays = 7 } = {}) {
    return cached(`ga4:${maxAgeDays}`, async () => {
      const { rows, meta } = await get('/ga4', { max_age_days: maxAgeDays });
      return {
        rows,
        asOfDate: meta.as_of_date || null,
        warning: meta.warning || null,
      };
    });
  },

  /**
   * Bir property icin gunluk zaman serisi.
   *
   * Yeni modelin /metrics endpoint'i sayesinde bu artik tek istek --
   * eskiden tum ga4_metrics tablosunu cekip JS'te filtreliyorduk.
   *
   * Returns: [{ report_date, active_users, new_users, event_counts }]
   */
  async getTimeseries(appPlatformId, days = 30) {
    const since = new Date(Date.now() - days * 86_400_000)
      .toISOString()
      .slice(0, 10);
    const { rows } = await get('/ga4-timeseries', {
      app_platform_id: appPlatformId,
      date_from: since,
    });
    return rows;
  },
};

// ---------------------------------------------------------------------------
// Sosyal medya
// ---------------------------------------------------------------------------

export const socialApi = {
  /**
   * Returns: { rows: [{ account, platform, follower_count, report_date }],
   *            asOfDate, warning }
   */
  async getFollowers({ platform = null, maxAgeDays = 7 } = {}) {
    return cached(`followers:${platform || ''}:${maxAgeDays}`, async () => {
      const params = { max_age_days: maxAgeDays };
      if (platform) params.platform = platform;
      const { rows, meta } = await get('/followers', params);
      return {
        rows,
        asOfDate: meta.as_of_date || null,
        warning: meta.warning || null,
      };
    });
  },

  /**
   * Returns: [{ account, platform, title, url, thumbnail_url, type,
   *             published_at }]
   */
  async getContents({ account = null, platform = null, limit = 1000 } = {}) {
    const key = `contents:${account || ''}:${platform || ''}:${limit}`;
    return cached(key, async () => {
      const params = { limit };
      if (account) params.account = account;
      if (platform) params.platform = platform;
      const { rows } = await get('/contents', params);
      return rows;
    });
  },

  /**
   * Returns: { rows: [{ account, platform, content_title, content_url,
   *                     content_type, report_date, views, likes, comments,
   *                     shares, impressions }],
   *            asOfDate, warning }
   */
  async getContentMetrics({
    account = null,
    platform = null,
    maxAgeDays = 7,
    limit = 1000,
  } = {}) {
    const key = `smmetrics:${account || ''}:${platform || ''}:${maxAgeDays}:${limit}`;
    return cached(key, async () => {
      const params = { max_age_days: maxAgeDays, limit };
      if (account) params.account = account;
      if (platform) params.platform = platform;
      const { rows, meta } = await get('/social-metrics', params);
      return {
        rows,
        asOfDate: meta.as_of_date || null,
        warning: meta.warning || null,
      };
    });
  },
};

// ---------------------------------------------------------------------------
// Birlesik analitik
//
// Bu iki namespace alt API'lerin uzerine kurulu -- ic mantigi
// supabaseApi.js'teki ile ayni, cunku girdi sekilleri korundu.
// ---------------------------------------------------------------------------

export const unifiedApi = {
  async getAnalytics() {
    const [downloads, ratings, ga4Result] = await Promise.all([
      storeMetricsApi.getDownloads(),
      storeMetricsApi.getRatings(),
      ga4Api.getLatest({ maxAgeDays: 30 }),
    ]);

    const ga4ByAppName = new Map();
    (ga4Result.rows || []).forEach((g) => ga4ByAppName.set(g.property_name, g));
    const ratingByAppPlatformId = new Map(
      ratings.map((r) => [r.app_platform_id, r]),
    );

    const out = downloads.map((d) => {
      const rating = ratingByAppPlatformId.get(d.app_platform_id);
      const ga4 =
        d.platform === PLATFORM_WEB ? null : ga4ByAppName.get(d.app_name);
      return {
        app_name: d.app_name,
        platform: d.platform,
        app_extid: d.app_extid,
        total_downloads: d.total_downloads,
        by_country: d.by_country,
        avg_rating: rating ? rating.avg_rating : null,
        latest_rating: rating ? rating.latest_rating : null,
        ga4_active_users: ga4 ? ga4.active_users : null,
        ga4_new_users: ga4 ? ga4.new_users : null,
        ga4_event_counts: ga4 ? ga4.event_counts : null,
      };
    });

    // Magaza satiri olmayan, yalnizca GA4'te bulunan web property'leri
    (ga4Result.rows || []).forEach((g) => {
      const matchedAlready = downloads.some(
        (d) => d.app_name === g.property_name && d.platform !== PLATFORM_WEB,
      );
      if (matchedAlready) return;
      out.push({
        app_name: g.property_name,
        platform: PLATFORM_WEB,
        app_extid: g.app_extid,
        total_downloads: null,
        by_country: [],
        avg_rating: null,
        latest_rating: null,
        ga4_active_users: g.active_users,
        ga4_new_users: g.new_users,
        ga4_event_counts: g.event_counts,
      });
    });

    return out;
  },
};

// ---------------------------------------------------------------------------
// Ana sayfa ozeti -- her metrigi platforma gore boler
// ---------------------------------------------------------------------------

export const dashboardApi = {
  async getPlatformSummary() {
    const [apps, downloads30d, ratings, subsResult, ga4Result, downloadsLifetime] =
      await Promise.all([
        appsApi.getAll(),
        storeMetricsApi.getDownloads({ timeseriesDays: 30 }),
        storeMetricsApi.getRatings(),
        subscriptionsApi.getLatest({ maxAgeDays: 30 }),
        ga4Api.getLatest({ maxAgeDays: 30 }),
        storeMetricsApi.getDownloads(),
      ]);

    // Platform basina uygulama sayisi
    const appCount = { ios: 0, android: 0, web: 0 };
    apps.forEach((a) => {
      (a.platforms || []).forEach((p) => {
        if (appCount[p.type] != null) appCount[p.type] += 1;
      });
    });

    const downloadTotalsLifetime = { ios: 0, android: 0, web: 0 };
    downloadsLifetime.forEach((d) => {
      if (downloadTotalsLifetime[d.platform] != null) {
        downloadTotalsLifetime[d.platform] += d.total_downloads;
      }
    });

    // 30 gunluk trend cizgileri
    const downloadTrendByDate = { ios: new Map(), android: new Map() };
    downloads30d.forEach((d) => {
      if (d.platform === PLATFORM_IOS || d.platform === PLATFORM_ANDROID) {
        (d.by_date || []).forEach((entry) => {
          const map = downloadTrendByDate[d.platform];
          map.set(
            entry.report_date,
            (map.get(entry.report_date) || 0) + entry.downloads,
          );
        });
      }
    });

    const ratingAverages = { ios: null, android: null };
    [PLATFORM_IOS, PLATFORM_ANDROID].forEach((plat) => {
      const platformRatings = ratings
        .filter((r) => r.platform === plat)
        .map((r) => r.avg_rating)
        .filter((v) => v != null);
      if (platformRatings.length) {
        ratingAverages[plat] = Number(
          (
            platformRatings.reduce((a, b) => a + b, 0) / platformRatings.length
          ).toFixed(2),
        );
      }
    });

    const subscribersByPlatform = { ios: 0, android: 0 };
    (subsResult.rows || []).forEach((row) => {
      if (subscribersByPlatform[row.platform] != null) {
        subscribersByPlatform[row.platform] += row.total_active_subscribers;
      }
    });

    const ga4Totals = (ga4Result.rows || []).reduce(
      (acc, r) => ({
        active_users: acc.active_users + (r.active_users || 0),
        new_users: acc.new_users + (r.new_users || 0),
        event_counts: acc.event_counts + (r.event_counts || 0),
      }),
      { active_users: 0, new_users: 0, event_counts: 0 },
    );

    const trend = {};
    [PLATFORM_IOS, PLATFORM_ANDROID].forEach((plat) => {
      trend[plat] = Array.from(downloadTrendByDate[plat].entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([report_date, downloads]) => ({ report_date, downloads }));
    });

    return {
      ios: {
        app_count: appCount.ios,
        total_downloads: downloadTotalsLifetime.ios,
        avg_rating: ratingAverages.ios,
        active_subscribers: subscribersByPlatform.ios,
        subs_as_of: subsResult.asOfDate,
      },
      android: {
        app_count: appCount.android,
        total_downloads: downloadTotalsLifetime.android,
        avg_rating: ratingAverages.android,
        active_subscribers: subscribersByPlatform.android,
      },
      web: {
        app_count: appCount.web,
        active_users: ga4Totals.active_users,
        new_users: ga4Totals.new_users,
        event_counts: ga4Totals.event_counts,
        as_of: ga4Result.asOfDate,
      },
      trend,
    };
  },
};

// ---------------------------------------------------------------------------
// Yeni modelin actigi ek erisim
// ---------------------------------------------------------------------------

export const metricsApi = {
  /**
   * Ham uzun-format olcumler. Yeni bir metrik tanimlandiginda bu
   * endpoint kod degisikligi olmadan onu da dondurur.
   *
   * Returns: [{ measurable_type, account_name, platform, post_title,
   *             metric_name, unit, value, measured_at }]
   */
  async getRaw({
    measurableType = null,
    metricName = null,
    account = null,
    platform = null,
    dateFrom = null,
    dateTo = null,
    limit = 1000,
  } = {}) {
    const params = { limit };
    if (measurableType) params.measurable_type = measurableType;
    if (metricName) params.metric_name = metricName;
    if (account) params.account = account;
    if (platform) params.platform = platform;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { rows } = await get('/metrics', params);
    return rows;
  },

  /**
   * Metrik katalogu: hangi metrikleri topluyoruz, hangi varlik tipi
   * icin gecerliler.
   *
   * Returns: [{ id, name, value_type, unit, description, applies_to: [] }]
   */
  async getDefinitions() {
    return cached('metric-defs', async () => {
      const { rows } = await get('/metric-defs');
      return rows;
    });
  },
};

const mrdsApi = {
  appsApi,
  storeMetricsApi,
  subscriptionsApi,
  ga4Api,
  socialApi,
  unifiedApi,
  dashboardApi,
  metricsApi,
  refreshCache,
};

export default mrdsApi;
